import { useEffect, useState } from "react";
import { Scale, Target } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI } from "../../lib/utils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { modelTitle } from "./modelLabels";

const AUTO = "__auto__";
const NO_COMPARE = "__none__";

const formatKg = (value) => String(value).replace(".", ",");

/**
 * Con quale modello e' valutato l'atleta in uno sport, e la scelta a mano (D13).
 *
 * Una riga sopra il profilo prestativo, nella vista del coach: dice quale
 * modello vale e di chi e', e lascia sceglierne un altro fra quelli che il
 * coach puo' vedere (il sistema, il suo, quelli pubblicati dal team) oppure
 * tornare alla regola automatica (D2). Serve soprattutto a risolvere i casi in
 * cui due coach dell'atleta hanno due modelli: li' vale il sistema finche'
 * qualcuno non sceglie.
 *
 * Il caricamento e' uno per atleta (tutti gli sport insieme); `onChanged` fa
 * ricaricare il profilo, perche' i livelli dipendono dal modello.
 *
 * Nei modelli per categoria di peso (D6b) una seconda riga dice su quale
 * categoria si leggono i livelli, ricavata dal peso del profilo, e lascia
 * scegliere un'altra categoria del suo genere per il confronto: serve a
 * decidere dove far gareggiare l'atleta. `onCompare({key, label, baseLabel})`
 * o `onCompare(null)`; il confronto lo carica e lo mostra il chiamante.
 */
export function AthleteModelBar({
	athleteId,
	sport,
	onChanged,
	compareKey = null,
	onCompare,
}) {
	const { t } = useLanguage();
	const [data, setData] = useState(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!athleteId) return undefined;
		let cancelled = false;
		fetchAPI(`/coach/athletes/${encodeURIComponent(athleteId)}/performance-models`)
			.then((payload) => {
				if (!cancelled) setData(payload);
			})
			.catch(() => {
				if (!cancelled) setData(null);
			});
		return () => {
			cancelled = true;
		};
	}, [athleteId]);

	const entry = (data?.sports || []).find((item) => item.sport === sport) ||
		(data?.sports || [])[0];
	if (!entry?.resolved) return null;

	const optionLabel = (model) =>
		model.is_system
			? t("scSystemModel")
			: `${modelTitle(model, t)} · ${model.owner?.name || ""}`;

	const change = async (value) => {
		setSaving(true);
		try {
			const payload = await fetchAPI(
				`/coach/athletes/${encodeURIComponent(athleteId)}/performance-models`,
				{
					method: "PUT",
					body: JSON.stringify({
						sport: entry.sport,
						model_id: value === AUTO ? null : value,
					}),
				},
			);
			setData(payload);
			toast.success(t("scAthleteModelSaved"));
			onChanged?.();
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setSaving(false);
		}
	};

	const resolved = entry.resolved;
	const bodyweight = entry.bodyweight || null;
	const byClass = bodyweight?.segmentation === "weight_class";
	const current = bodyweight?.weight_class || null;
	const missing = bodyweight?.missing || [];
	const compareOptions = (bodyweight?.classes || []).filter(
		(item) => item.key !== current?.key,
	);

	const pickCompare = (value) => {
		if (value === NO_COMPARE) {
			onCompare?.(null);
			return;
		}
		const chosen = compareOptions.find((item) => item.key === value);
		if (chosen) {
			onCompare?.({
				key: chosen.key,
				label: chosen.label,
				baseLabel: current?.label || null,
			});
		}
	};

	let missingText = t("scClassNoGender");
	if (missing.includes("classes")) missingText = t("scClassNoClasses");
	else if (missing.includes("weight")) missingText = t("scClassNoWeight");

	return (
		<div className="space-y-2">
			<div className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-secondary/30 px-3 py-2 text-xs">
				<Target className="w-3.5 h-3.5 text-volt shrink-0" />
				<span className="text-muted-foreground">{t("scEvaluatedWith")}</span>
				<span className="font-medium text-foreground">
					{resolved.is_system
						? t("scSystemModel")
						: `${modelTitle(resolved, t)} · ${resolved.owner?.name || ""}`}
				</span>
				{entry.manual_model_id && (
					<span className="text-muted-foreground">({t("scChosenByHand")})</span>
				)}
				<Select
					value={entry.manual_model_id || AUTO}
					onValueChange={change}
					disabled={saving}
				>
					<SelectTrigger
						className="ml-auto h-7 w-auto min-w-[150px] max-w-full text-xs bg-background border-border rounded-sm"
						aria-label={t("scChooseModel")}
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						<SelectItem value={AUTO}>{t("scAutomatic")}</SelectItem>
						{entry.options.map((model) => (
							<SelectItem key={model.model_id} value={model.model_id}>
								{optionLabel(model)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{byClass && (
				<div
					className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-secondary/30 px-3 py-2 text-xs"
					data-testid="athlete-weight-class"
				>
					<Scale className="w-3.5 h-3.5 text-volt shrink-0" />
					{current ? (
						<span>
							<span className="text-muted-foreground">{t("scClassOf")} </span>
							<span className="font-medium text-foreground">{current.label}</span>
							{bodyweight.weight != null && (
								<span className="text-muted-foreground">
									{" · "}
									{t("scClassWeight").replace("{weight}", formatKg(bodyweight.weight))}
								</span>
							)}
						</span>
					) : (
						<span className="text-amber-300">{missingText}</span>
					)}
					{onCompare && compareOptions.length > 0 && (
						<Select value={compareKey || NO_COMPARE} onValueChange={pickCompare}>
							<SelectTrigger
								className="ml-auto h-7 w-auto min-w-[150px] max-w-full text-xs bg-background border-border rounded-sm"
								aria-label={t("scCompareClass")}
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								<SelectItem value={NO_COMPARE}>{t("scCompareClass")}</SelectItem>
								{compareOptions.map((item) => (
									<SelectItem key={item.key} value={item.key}>
										{t("scCompareWith").replace("{label}", item.label)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				</div>
			)}
		</div>
	);
}
